import { Link } from "react-router-dom";
import { Briefcase, CheckCircle2, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { fadeUp, fadeRight, fadeLeft } from "@/lib/motion";
import { useSeo } from "@/hooks/useSeo";
import { EXPERTS } from "@/data/content";

export default function Team() {
  useSeo(
    "Our Team | Certification Experts | AutoDrive Tech Solutions",
    "Meet AutoDrive's certification experts — former ICAT professionals specialising in homologation and electric vehicle certification."
  );

  return (
    <>
      <PageHero
        overline="Our Team"
        title="The Experts Behind Your Certification"
        subtitle="Former agency professionals with hands-on experience across homologation and EV certification."
        crumb="Our Team"
      />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 space-y-24">
          {EXPERTS.map((e, idx) => (
            <div
              key={e.slug}
              id={e.slug}
              data-testid={`team-bio-${e.slug}`}
              className="scroll-mt-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start"
            >
              <Reveal variants={idx % 2 === 0 ? fadeRight : fadeLeft} className={`lg:col-span-5 ${idx % 2 === 0 ? "" : "lg:order-2"}`}>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
                  <img src={e.image} alt={`${e.name}, ${e.shortRole}`} className="w-full h-[460px] object-cover object-top" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-xs font-semibold text-[#1E40AF]">
                      <Briefcase className="w-3.5 h-3.5" /> {e.experience} Experience
                    </span>
                  </div>
                </div>
              </Reveal>

              <div className={`lg:col-span-7 ${idx % 2 === 0 ? "" : "lg:order-1"}`}>
                <Reveal>
                  <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#1E40AF]">{e.former}</span>
                  <h2 className="mt-3 font-display font-bold text-4xl text-[#0F172A] tracking-tight">{e.name}</h2>
                  <p className="mt-2 text-lg font-semibold text-[#1E40AF]">{e.role}</p>
                  <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                    <span className="text-slate-500"><span className="font-semibold text-[#0F172A]">Experience:</span> {e.experience}</span>
                    <span className="text-slate-500"><span className="font-semibold text-[#0F172A]">Previous Association:</span> {e.association}</span>
                  </div>
                </Reveal>

                <Reveal className="mt-6">
                  <h3 className="font-display font-semibold text-sm tracking-wide text-[#0F172A] uppercase mb-3">Profile Overview</h3>
                  <div className="space-y-4 text-slate-600 leading-relaxed">
                    {e.overview.map((p, i) => (<p key={i}>{p}</p>))}
                  </div>
                </Reveal>

                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <Reveal variants={fadeUp}>
                    <h3 className="font-display font-semibold text-sm tracking-wide text-[#0F172A] uppercase mb-4">Core Competencies</h3>
                    <ul className="space-y-3">
                      {e.competencies.map((c) => (
                        <li key={c} className="flex items-start gap-3 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-[#1E40AF] shrink-0 mt-0.5" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                  <Reveal variants={fadeUp} custom={1}>
                    <h3 className="font-display font-semibold text-sm tracking-wide text-[#0F172A] uppercase mb-4">Professional Attributes</h3>
                    <ul className="space-y-3">
                      {e.attributes.map((a) => (
                        <li key={a} className="flex items-start gap-3 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                </div>

                <Reveal className="mt-8">
                  <Link
                    to="/book-consultation"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1E40AF] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 transition-colors"
                  >
                    Work With {e.name.split(" ")[0]} <ArrowRight className="w-4 h-4" />
                  </Link>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}
